import type React from "react";

/* focusableWhenDisabled: the one implementation (button-pressed-busy). Its
 * meaning and contract live in the JSDoc on Button's `focusableWhenDisabled`
 * prop. Every part that takes the prop reads this helper, so the behaviour is
 * never written twice. Engine tier, internal: not exported from the barrel. */

export interface FocusableDisabledInput<E extends Element> {
  /** The control cannot act now: disabled, or busy (loading). */
  inactive: boolean;
  /** Keep the control in the Tab order while it is inactive. */
  focusableWhenDisabled: boolean;
  /** The caller's click handler. It never runs while the control is inactive
   *  and focusable. */
  onClick?: React.MouseEventHandler<E> | undefined;
}

export interface FocusableDisabledProps<E extends Element> {
  disabled?: boolean;
  "aria-disabled"?: true;
  onClick?: React.MouseEventHandler<E> | undefined;
}

/** Stops the click and its default action: a submit button does not submit
 *  its form, and a link does not navigate. */
function blockClick(event: React.MouseEvent): void {
  event.preventDefault();
}

/**
 * The disabled attributes and the click guard for a control.
 *
 * - Active: `disabled: false` and the caller's `onClick`, unchanged.
 * - Inactive, not focusable: the native `disabled` attribute (today's
 *   behaviour). The browser removes the control from the Tab order.
 * - Inactive and focusable: `aria-disabled="true"` and no native `disabled`,
 *   so the control stays in the Tab order and keeps focus. The click handler
 *   calls `preventDefault()` and does not call the caller's `onClick`.
 */
export function focusableDisabledProps<E extends Element>({
  inactive,
  focusableWhenDisabled,
  onClick,
}: FocusableDisabledInput<E>): FocusableDisabledProps<E> {
  if (inactive && focusableWhenDisabled) {
    return { "aria-disabled": true, onClick: blockClick };
  }
  return { disabled: inactive, onClick };
}
